import dayjs from 'dayjs'
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronLeftIcon } from '@heroicons/react/24/outline'
import {
    ChatTaskStatusView,
    useGetChatTaskStatusLazyQuery,
    useStartChatMutation
} from '../../../api/client'
import ChatAssistantComposer from '../../../components/content/search/chatAssistant/ChatAssistantComposer'
import ChatAssistantContentDrawer from '../../../components/content/search/chatAssistant/ChatAssistantContentDrawer'
import ChatAssistantMessagesList from '../../../components/content/search/chatAssistant/ChatAssistantMessagesList'
import ChatAssistantPromptSuggestions from '../../../components/content/search/chatAssistant/ChatAssistantPromptSuggestions'
import ChatAssistantSessionInfoCard from '../../../components/content/search/chatAssistant/ChatAssistantSessionInfoCard'
import {
    ChatSessionInfo,
    LocalChatMessage,
    PromptSuggestion,
    sanitizeErrorMessage
} from '../../../components/content/search/chatAssistant/types'
import SearchParameters from '../../../components/content/search/searchParameters'

type ChatStepProps = {
    accountId: number
    sessionId: string
    itemCount: number
    searchParameters: SearchParameters
    onBack: () => void
    earliest?: string | null
    latest?: string | null
}

export default function ChatStep({
    accountId,
    sessionId,
    itemCount,
    searchParameters,
    onBack,
    earliest,
    latest
}: ChatStepProps) {
    const nextMessageId = useRef(1)
    const textareaRef = useRef<HTMLTextAreaElement | null>(null)
    const messagesEndRef = useRef<HTMLDivElement | null>(null)

    const [messages, setMessages] = useState<LocalChatMessage[]>([])
    const [draft, setDraft] = useState('')
    const [isThinking, setIsThinking] = useState(false)
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [selectedContentId, setSelectedContentId] = useState<number | null>(null)

    const [startChat] = useStartChatMutation()
    const [getChatTaskStatus] = useGetChatTaskStatusLazyQuery({
        fetchPolicy: 'no-cache'
    })

    const sessionInfo: ChatSessionInfo = useMemo(
        () => ({
            itemCount,
            earliest: earliest ?? null,
            latest: latest ?? null,
            summary: `Dataset with ${itemCount.toLocaleString()} items ready for analysis.`
        }),
        [itemCount, earliest, latest]
    )

    const promptSuggestions = useMemo<ReadonlyArray<PromptSuggestion>>(
        () => [
            {
                id: 'summary',
                title: 'Summarise the dataset',
                description:
                    'Provide a concise overview of the key themes and metrics in this dataset.',
                prompt: 'Summarise the dataset into key themes, notable metrics, and immediate takeaways.'
            },
            {
                id: 'top-performers',
                title: 'Top-performing posts',
                description: 'Identify standout posts and explain what made them successful.',
                prompt: 'Which posts performed best in this dataset? Highlight why they succeeded and include actionable recommendations for replicating the results.'
            },
            {
                id: 'sentiment-watch',
                title: 'Sentiment shifts',
                description: 'Spot sentiment changes and what they might mean.',
                prompt: 'Highlight any emerging sentiment shifts in the dataset, cite examples, and recommend how the team should respond.'
            }
        ],
        []
    )

    const profileCount = searchParameters.selectedAccountIds.length

    const formatDateTimeValue = (value?: string | null) => {
        if (!value) return 'n/a'
        const parsed = dayjs(value)
        return parsed.isValid() ? parsed.format('MMM D, YYYY HH:mm') : value
    }

    const createMessage = useCallback(
        (role: 'user' | 'assistant', content: string): LocalChatMessage => {
            const id = `msg-${nextMessageId.current}`
            nextMessageId.current += 1
            return { id, role, content }
        },
        []
    )

    const sendMessage = useCallback(
        async (content: string) => {
            const trimmed = content.trim()
            if (!trimmed || !sessionId) return

            const userEntry = createMessage('user', trimmed)
            setMessages((current) => [...current, userEntry])

            try {
                setIsThinking(true)

                const startResult = await startChat({
                    variables: {
                        accountId,
                        sessionId,
                        message: trimmed
                    }
                })

                const taskId = startResult.data?.startChat?.taskId
                if (!taskId) {
                    throw new Error('No task ID returned from server')
                }

                // Poll for result (max 2 minutes = 120 attempts at 1 second each)
                const maxAttempts = 120
                let attempts = 0

                while (attempts < maxAttempts) {
                    await new Promise((resolve) => setTimeout(resolve, 1000))

                    const statusResult = await getChatTaskStatus({
                        variables: { taskId }
                    })

                    const status = statusResult.data?.getChatTaskStatus
                    if (!status) {
                        attempts++
                        continue
                    }

                    if (status.status === ChatTaskStatusView.Success && status.result) {
                        const reply = status.result.reply
                        setMessages((current) => [...current, createMessage('assistant', reply)])
                        return
                    }

                    if (status.status === ChatTaskStatusView.Failed) {
                        throw new Error(
                            sanitizeErrorMessage(status.errorMessage || 'Chat task failed')
                        )
                    }

                    attempts++
                }

                throw new Error('Chat task timed out after 2 minutes')
            } catch (error) {
                const rawMessage =
                    error instanceof Error
                        ? error.message
                        : 'Unknown error while contacting the assistant.'
                const errorMessage = sanitizeErrorMessage(rawMessage)
                setMessages((current) => [
                    ...current,
                    createMessage('assistant', `Sorry, something went wrong: ${errorMessage}`)
                ])
            } finally {
                setIsThinking(false)
            }
        },
        [accountId, createMessage, getChatTaskStatus, sessionId, startChat]
    )

    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, [])

    useEffect(() => {
        scrollToBottom()
    }, [messages, isThinking, scrollToBottom])

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        const trimmed = draft.trim()
        if (!trimmed) return

        setDraft('')
        void sendMessage(trimmed)
    }

    const handleSuggestionSelect = (suggestion: string) => {
        setDraft(suggestion)
        textareaRef.current?.focus()
    }

    const handleContentReferenceClick = useCallback((contentId: number) => {
        setSelectedContentId(contentId)
        setDrawerOpen(true)
    }, [])

    const handleDrawerClose = useCallback(() => {
        setDrawerOpen(false)
    }, [])

    const isSendDisabled = draft.trim().length === 0 || isThinking
    const hasUserMessages = messages.some((message) => message.role === 'user')
    const shouldShowSuggestions = !hasUserMessages

    return (
        <>
            <div className="flex min-h-0 w-full flex-1 flex-col">
                {/* Scrollable content area */}
                <div className="min-h-0 flex-1 overflow-y-auto">
                    <div className="mx-auto w-full max-w-6xl px-4 py-6">
                        {/* Back button */}
                        <div className="mb-6">
                            <button
                                type="button"
                                onClick={onBack}
                                className="inline-flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                            >
                                <ChevronLeftIcon className="h-4 w-4" />
                                Start new conversation
                            </button>
                        </div>

                        <section className="flex w-full flex-col gap-5">
                            <ChatAssistantSessionInfoCard
                                sessionInfo={sessionInfo}
                                profileCount={profileCount}
                                formatDateTimeValue={formatDateTimeValue}
                            />

                            {shouldShowSuggestions && (
                                <ChatAssistantPromptSuggestions
                                    suggestions={promptSuggestions}
                                    onSelect={handleSuggestionSelect}
                                />
                            )}

                            <ChatAssistantMessagesList
                                messages={messages}
                                isLoading={isThinking}
                                isPreparing={false}
                                onContentReferenceClick={handleContentReferenceClick}
                                messagesEndRef={messagesEndRef}
                            />
                        </section>
                    </div>
                </div>

                {/* Fixed composer at bottom - full width */}
                <div className="flex-shrink-0 border-t border-gray-200 bg-white px-4 py-4">
                    <div className="mx-auto w-full max-w-6xl">
                        <ChatAssistantComposer
                            draft={draft}
                            onDraftChange={(value) => setDraft(value)}
                            onSubmit={handleSubmit}
                            textareaRef={textareaRef}
                            isSendDisabled={isSendDisabled}
                        />
                    </div>
                </div>
            </div>

            {/* Content drawer */}
            <ChatAssistantContentDrawer
                open={drawerOpen}
                onClose={handleDrawerClose}
                contentId={selectedContentId}
            />
        </>
    )
}
